// Pure gameplay definitions shared by the browser and authoritative module.
import { Facing, GameConstants, InputButtons, inputFacingShift } from './constants';

/** Anything that says what blocks movement (unloaded tiles do). */
export interface CollisionMap {
  /** Whether the whole tile blocks: outside the map or unloaded, no ground, full-tile collision, footprints, blockers. */
  isSolidTile(tx: number, ty: number): boolean;
  /** Custom (smaller or larger than a tile) collision touching the tile: world-pixel boxes [x0, y0, x1, y1], half-open,
   * appended to out. A big object's boxes appear in every tile they touch. */
  partialBoxes?(tx: number, ty: number, out: number[]): void;
}

/** Whether a world pixel point is inside anything solid (projectiles, drops). */
export function solidAt(map: CollisionMap, x: number, y: number): boolean {
  const tx = Math.floor(x / GameConstants.tileSize), ty = Math.floor(y / GameConstants.tileSize);
  if (map.isSolidTile(tx, ty)) return true;
  if (!map.partialBoxes) return false;
  boxes.length = 0; map.partialBoxes(tx, ty, boxes);
  for (let i = 0; i < boxes.length; i += 4) if (x >= boxes[i] && y >= boxes[i + 1] && x < boxes[i + 2] && y < boxes[i + 3]) return true;
  return false;
}

/** Scratch for partial box queries (reused by every query; never carries state between them). */
const boxes: number[] = [];

export const scale = 256;
const tile = GameConstants.tileSize * scale;

export interface MotorState {
  /** Feet position in 1/256 px. */
  x: number;
  y: number;
  facing: Facing;
  moving: boolean;
  running: boolean;
  /** Stamina in 1/30 points, 0..maxStaminaUnits. */
  staminaUnits: number;
  /** Ticks left before stamina starts regenerating after sprinting. */
  staminaDelay: number;
  /** Ran dry: sprinting is locked out until stamina recovers past a threshold. */
  exhausted: boolean;
  /** Ticks left stunned: no moving or turning (the module sets it when a guard breaks). */
  stunTicks: number;
}

export const Motor = {
  walkStep: 614,
  runStep: 1024,
  walkStepDiagonal: 434,
  runStepDiagonal: 724,
  halfWidthFixed: 1152, // 4.5 px
  heightFixed: 1536,    // 6 px
  cornerSlide: 5,       // px
  staminaUnitsPerPoint: 30,
  maxStaminaUnits: 3000,
  staminaDrainPerTick: 22,
  staminaRegenPerTick: 18,
  staminaRegenDelayTicks: 22,
  exhaustionRecoverUnits: 750,
  maxStamina: 100,
  /** Pixels per second. */
  walkSpeed: 614 * 30 / 256,
  runSpeed: 1024 * 30 / 256,
};

/** Pixels → 1/256 px (rounds half to even). */
export function toFixed(pixels: number): number {
  const v = Math.fround(Math.fround(pixels) * scale);
  const r = Math.round(v);
  return Math.abs(v % 1) === 0.5 && r % 2 !== 0 ? r - 1 : r;
}

export const newMotorState = (x: number, y: number, facing = Facing.Down): MotorState => ({
  x, y, facing, moving: false, running: false, staminaUnits: Motor.maxStaminaUnits, staminaDelay: 0, exhausted: false, stunTicks: 0,
});

export function step(s: MotorState, buttons: number, map: CollisionMap): void {
  if (s.stunTicks > 0) {
    s.stunTicks--;
    s.moving = s.running = false;
    updateStamina(s, false);
    return;
  }
  if (buttons & InputButtons.Face) s.facing = ((buttons & InputButtons.FacingMask) >> inputFacingShift) as Facing;
  let dx = 0, dy = 0;
  if (buttons & InputButtons.Left) dx -= 1;
  if (buttons & InputButtons.Right) dx += 1;
  if (buttons & InputButtons.Up) dy -= 1;
  if (buttons & InputButtons.Down) dy += 1;

  const wantsRun = (buttons & InputButtons.Run) !== 0 && (dx !== 0 || dy !== 0);
  const running = wantsRun && !s.exhausted && s.staminaUnits > 0;
  s.running = running;

  if (dx === 0 && dy === 0) {
    s.moving = false;
    updateStamina(s, false);
    return;
  }

  s.facing = resolveFacing(s.facing, dx, dy);
  const diagonal = dx !== 0 && dy !== 0;
  const stepSize = diagonal ? (running ? Motor.runStepDiagonal : Motor.walkStepDiagonal) : (running ? Motor.runStep : Motor.walkStep);

  const startX = s.x, startY = s.y;
  let [x, y] = moveAxis(s.x, s.y, dx * stepSize, true, map, dy === 0, stepSize);
  [x, y] = moveAxis(x, y, dy * stepSize, false, map, dx === 0, stepSize);
  s.x = x;
  s.y = y;
  s.moving = x !== startX || y !== startY;
  updateStamina(s, running && s.moving); // sprinting into a wall doesn't cost stamina
}

function updateStamina(s: MotorState, sprinted: boolean): void {
  if (sprinted) {
    s.staminaUnits -= Motor.staminaDrainPerTick;
    s.staminaDelay = Motor.staminaRegenDelayTicks;
    if (s.staminaUnits <= 0) {
      s.staminaUnits = 0;
      s.exhausted = true;
    }
    return;
  }
  if (s.staminaDelay > 0) {
    s.staminaDelay--;
    return;
  }
  s.staminaUnits = Math.min(Motor.maxStaminaUnits, s.staminaUnits + Motor.staminaRegenPerTick);
  if (s.exhausted && s.staminaUnits >= Motor.exhaustionRecoverUnits) s.exhausted = false;
}

function resolveFacing(current: Facing, dx: number, dy: number): Facing {
  if (dx !== 0 && dy !== 0) {
    if (current === Facing.Up && dy < 0) return Facing.Up;
    if (current === Facing.Down && dy > 0) return Facing.Down;
    return dx < 0 ? Facing.Left : Facing.Right;
  }
  if (dx < 0) return Facing.Left;
  if (dx > 0) return Facing.Right;
  return dy < 0 ? Facing.Up : Facing.Down;
}

function moveAxis(x: number, y: number, delta: number, horizontal: boolean, map: CollisionMap, allowSlide: boolean, stepSize: number): [number, number] {
  if (delta === 0) return [x, y];
  const tx = horizontal ? x + delta : x, ty = horizontal ? y : y + delta;
  if (!overlaps(tx, ty, map)) return [tx, ty];
  // Escape rule: already embedded in something solid, move freely until clear.
  if (overlaps(x, y, map)) return [tx, ty];

  // Whole tiles clamp to the tile edge entered; custom boxes to their own nearest edge; the nearer of the two wins.
  let clamped: [number, number] | null = tileBlocks(tx, ty, map) ? clampToEdge(x, y, delta, horizontal) : null;
  const box = clampToBoxes(x, y, tx, ty, delta, horizontal, map);
  if (box) clamped = !clamped ? box : (horizontal ? (delta > 0 ? box[0] < clamped[0] : box[0] > clamped[0]) : (delta > 0 ? box[1] < clamped[1] : box[1] > clamped[1])) ? box : clamped;
  if (!clamped || overlaps(clamped[0], clamped[1], map)) clamped = [x, y];

  if (allowSlide) {
    for (let px = 1; px <= Motor.cornerSlide; px++) {
      for (let sign = -1; sign <= 1; sign += 2) {
        const off = sign * px * scale;
        const ox = horizontal ? 0 : off, oy = horizontal ? off : 0;
        if (overlaps(x + ox, y + oy, map)) continue;
        if (overlaps(tx + ox, ty + oy, map)) continue;
        const nudge = Math.min(stepSize, px * scale) * sign;
        const sx = horizontal ? x : x + nudge, sy = horizontal ? y + nudge : y;
        return overlaps(sx, sy, map) ? clamped : [sx, sy];
      }
    }
  }
  return clamped;
}

function clampToEdge(x: number, y: number, delta: number, horizontal: boolean): [number, number] {
  if (horizontal) {
    if (delta > 0) {
      const edge = floorDiv(x + delta + Motor.halfWidthFixed, tile) * tile;
      return [Math.max(x, edge - Motor.halfWidthFixed - 1), y];
    }
    const edge = (floorDiv(x + delta - Motor.halfWidthFixed, tile) + 1) * tile;
    return [Math.min(x, edge + Motor.halfWidthFixed + 1), y];
  }
  if (delta > 0) {
    const edge = floorDiv(y + delta, tile) * tile;
    return [x, Math.max(y, edge - 1)];
  }
  const edge = (floorDiv(y + delta - Motor.heightFixed, tile) + 1) * tile;
  return [x, Math.min(y, edge + Motor.heightFixed + 1)];
}

/** Whether a whole solid tile touches the collision box with its feet at (x, y). */
function tileBlocks(x: number, y: number, map: CollisionMap): boolean {
  const x0 = floorDiv(x - Motor.halfWidthFixed, tile), x1 = floorDiv(x + Motor.halfWidthFixed, tile);
  const y0 = floorDiv(y - Motor.heightFixed, tile), y1 = floorDiv(y, tile);
  for (let ty = y0; ty <= y1; ty++) for (let tx = x0; tx <= x1; tx++) if (map.isSolidTile(tx, ty)) return true;
  return false;
}

/** Whether the collision box with its feet at (x, y) (1/256 px) touches box i of the scratch (world pixels). */
const touchesBox = (x: number, y: number, i: number) =>
  x - Motor.halfWidthFixed < boxes[i + 2] * scale && x + Motor.halfWidthFixed >= boxes[i] * scale &&
  y - Motor.heightFixed < boxes[i + 3] * scale && y >= boxes[i + 1] * scale;

/** Moving from (x, y) toward (tx, ty) along one axis: stop just short of the nearest custom box the target touches. */
function clampToBoxes(x: number, y: number, tx: number, ty: number, delta: number, horizontal: boolean, map: CollisionMap): [number, number] | null {
  if (!map.partialBoxes) return null;
  let limit: number | null = null;
  const cx0 = floorDiv(tx - Motor.halfWidthFixed, tile), cx1 = floorDiv(tx + Motor.halfWidthFixed, tile);
  const cy0 = floorDiv(ty - Motor.heightFixed, tile), cy1 = floorDiv(ty, tile);
  for (let cy = cy0; cy <= cy1; cy++) for (let cx = cx0; cx <= cx1; cx++) {
    boxes.length = 0; map.partialBoxes(cx, cy, boxes);
    for (let i = 0; i < boxes.length; i += 4) {
      if (!touchesBox(tx, ty, i)) continue;
      const edge = (horizontal ? (delta > 0 ? boxes[i] : boxes[i + 2]) : (delta > 0 ? boxes[i + 1] : boxes[i + 3])) * scale;
      limit = limit === null ? edge : delta > 0 ? Math.min(limit, edge) : Math.max(limit, edge);
    }
  }
  if (limit === null) return null;
  if (horizontal) return delta > 0 ? [Math.max(x, limit - Motor.halfWidthFixed - 1), y] : [Math.min(x, limit + Motor.halfWidthFixed + 1), y];
  return delta > 0 ? [x, Math.max(y, limit - 1)] : [x, Math.min(y, limit + Motor.heightFixed + 1)];
}

/** Non-player movement with the same collision clamp but no corner sliding (mobs/knockback).
 * Pixel conversions match the legacy float32 boundary; the collision step itself is integer-only.
 */
export function moveWithCollision(x: number, y: number, dx: number, dy: number, map: CollisionMap): [number, number] {
  let [fx, fy] = moveAxis(toFixed(x), toFixed(y), toFixed(dx), true, map, false, 0);
  [fx, fy] = moveAxis(fx, fy, toFixed(dy), false, map, false, 0);
  return [Math.fround(fx / scale), Math.fround(fy / scale)];
}

/** Whether a player's collision box with its feet at (x, y) (1/256 px) touches a solid tile or custom collision box. */
export function overlaps(x: number, y: number, map: CollisionMap): boolean {
  const x0 = floorDiv(x - Motor.halfWidthFixed, tile), x1 = floorDiv(x + Motor.halfWidthFixed, tile);
  const y0 = floorDiv(y - Motor.heightFixed, tile), y1 = floorDiv(y, tile);
  for (let ty = y0; ty <= y1; ty++)
    for (let tx = x0; tx <= x1; tx++)
      if (map.isSolidTile(tx, ty)) return true;
  if (!map.partialBoxes) return false;
  for (let ty = y0; ty <= y1; ty++)
    for (let tx = x0; tx <= x1; tx++) {
      boxes.length = 0; map.partialBoxes(tx, ty, boxes);
      for (let i = 0; i < boxes.length; i += 4) if (touchesBox(x, y, i)) return true;
    }
  return false;
}

export const floorDiv = (a: number, b: number) => Math.floor(a / b);

/** Tile the feet (in pixels) are standing on. */
export const feetTile = (x: number, y: number): [number, number] =>
  [Math.floor(x / GameConstants.tileSize), Math.floor((y - 1) / GameConstants.tileSize)];

/** Four-way facing for a movement or aim direction. */
export const facingFor = (dx: number, dy: number): Facing =>
  Math.abs(dx) >= Math.abs(dy) ? (dx < 0 ? Facing.Left : Facing.Right) : (dy < 0 ? Facing.Up : Facing.Down);
